import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Link, LangHint } from './PublishedLink';
import { SOCIAL_LINKS } from '../constants/socialLinks';
import { NAV_GROUPS, NavGroup } from '../constants/navigation';
import SocialIcon from './SocialIcon';
import LanguageSwitcher from './LanguageSwitcher';

const isGroupActive = (group: NavGroup, pathname: string): boolean => {
  if (group.to) {
    return pathname === group.to;
  }
  return Boolean(group.items?.some((item) => item.to === pathname));
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const immersive = location.pathname === '/' && !scrolled && !isOpen;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    setOpenGroup(null);
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!openGroup) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenGroup(null);
      }
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenGroup(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [openGroup]);

  return (
    <header className={`${immersive ? 'desert-header' : ''} fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center -my-4">
            <Link to="/" langHint={false} className="transition-transform hover:scale-105 duration-300">
              <img
                src="/logo.png"
                alt={t('header.logoAlt')}
                className={`transition-all duration-300 ${scrolled ? 'h-10' : 'h-12'} w-auto`}
              />
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden focus:outline-none focus:ring-2 focus:ring-primary-500 p-2 rounded-md"
            aria-expanded={isOpen}
            aria-label={t('header.openMenu')}
          >
            <span className={`block w-6 h-0.5 bg-black mb-1 transition-transform duration-300 ${isOpen ? 'transform rotate-45 translate-y-1.5' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-black mb-1 transition-opacity duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-0.5 bg-black transition-transform duration-300 ${isOpen ? 'transform -rotate-45 -translate-y-1.5' : ''}`}></span>
          </button>

          {/* Desktop menu */}
          <nav ref={navRef} className="hidden lg:flex items-center">
            <ul className="flex items-center space-x-7">
              {NAV_GROUPS.map((group) => {
                const active = isGroupActive(group, location.pathname);

                if (group.to) {
                  return (
                    <li key={group.labelKey}>
                      <Link
                        to={group.to}
                        className={`text-gray-800 hover:text-primary transition-colors relative whitespace-nowrap ${active ? 'font-medium' : ''}`}
                      >
                        {t(group.labelKey)}
                        {active && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary rounded-full"></span>}
                      </Link>
                    </li>
                  );
                }

                const expanded = openGroup === group.labelKey;

                return (
                  <li
                    key={group.labelKey}
                    className="relative"
                    onMouseEnter={() => setOpenGroup(group.labelKey)}
                    onMouseLeave={() => setOpenGroup(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenGroup(expanded ? null : group.labelKey)}
                      className={`flex items-center gap-1 text-gray-800 hover:text-primary transition-colors relative whitespace-nowrap ${active ? 'font-medium' : ''}`}
                      aria-expanded={expanded}
                      aria-haspopup="true"
                    >
                      {t(group.labelKey)}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                      {active && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary rounded-full"></span>}
                    </button>

                    {expanded && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72">
                        <div className="bg-white rounded-xl shadow-xl ring-1 ring-black/5 py-2">
                          {group.items?.map((item) => (
                            <Link
                              key={item.to}
                              to={item.to}
                              langHint={false}
                              className={`block px-4 py-2.5 hover:bg-orange-50 transition-colors ${
                                location.pathname === item.to ? 'bg-orange-50' : ''
                              }`}
                            >
                              <span className="block text-sm font-medium text-gray-800">
                                {t(item.labelKey)}
                                <LangHint to={item.to} />
                              </span>
                              {item.descriptionKey && (
                                <span className="block text-xs text-gray-500 mt-0.5">{t(item.descriptionKey)}</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <LanguageSwitcher className="ml-7 text-sm font-medium text-gray-600 hover:text-primary transition-colors" />
            <div className="flex items-center ml-7 space-x-4">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-800 hover:text-primary transition-colors transform hover:scale-110 duration-300"
                  aria-label={social.name}
                >
                  <SocialIcon social={social} className="w-5 h-5" />
                </a>
              ))}
            </div>
          </nav>
        </div>

        {/* Mobile menu */}
        <div
          className={`fixed inset-0 bg-white z-40 overflow-y-auto transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} lg:hidden`}
          aria-hidden={!isOpen}
        >
          <div className="flex justify-end p-4">
            <button
              onClick={() => setIsOpen(false)}
              className="focus:outline-none focus:ring-2 focus:ring-primary-500 p-2 rounded-md"
              aria-label={t('header.closeMenu')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="px-8 pb-16">
            {NAV_GROUPS.map((group) => (
              <div key={group.labelKey} className="mb-7">
                {group.to ? (
                  <Link
                    to={group.to}
                    className={`block text-xl font-display ${
                      location.pathname === group.to ? 'text-primary font-medium' : 'text-gray-800'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t(group.labelKey)}
                  </Link>
                ) : (
                  <>
                    <p className="text-xs tracking-[0.2em] text-gray-400 mb-3">{t(group.labelKey)}</p>
                    <div className="space-y-3 border-l border-gray-200 pl-4">
                      {group.items?.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={`block text-lg ${
                            location.pathname === item.to ? 'text-primary font-medium' : 'text-gray-800'
                          }`}
                          onClick={() => setIsOpen(false)}
                        >
                          {t(item.labelKey)}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
            <LanguageSwitcher className="inline-block mt-8 text-sm font-medium text-gray-600 hover:text-primary transition-colors" />
            <div className="flex space-x-6 mt-10">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-800 hover:text-primary transition-colors"
                  aria-label={social.name}
                >
                  <SocialIcon social={social} className="w-7 h-7" />
                </a>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
